import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { Toast, type ToastData, type ToastVariant } from '@/components/ui/Toast'
import { useTranslation } from '@/i18n/useTranslation'
import { cn } from '@/lib/cn'
import { setApiErrorHandler, setApiErrorFallbacks } from '@/lib/apiError'

// ---------------------------------------------------------------------------
// Public API types
// ---------------------------------------------------------------------------

export interface AddToastOptions {
  variant: ToastVariant
  title: string
  description?: string
  /** Auto-dismiss delay in ms. Defaults to 8 000. */
  duration?: number
}

export interface ToastContextValue {
  toast: (options: AddToastOptions) => void
  dismiss: (id: string) => void
  dismissAll: () => void
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ToastContext = createContext<ToastContextValue | null>(null)

// ---------------------------------------------------------------------------
// Reducer
// ---------------------------------------------------------------------------

const MAX_VISIBLE = 4
const DEFAULT_DURATION = 12_000

type ToastAction =
  | { type: 'ADD'; payload: ToastData }
  | { type: 'START_REMOVE'; id: string }
  | { type: 'REMOVE'; id: string }
  | { type: 'REMOVE_ALL' }

function reducer(state: ToastData[], action: ToastAction): ToastData[] {
  switch (action.type) {
    case 'ADD': {
      const trimmed = state.length >= MAX_VISIBLE ? state.slice(1) : state
      return [...trimmed, action.payload]
    }
    case 'START_REMOVE':
      return state.map(t => (t.id === action.id ? { ...t, removing: true } : t))
    case 'REMOVE':
      return state.filter(t => t.id !== action.id)
    case 'REMOVE_ALL':
      return []
    default:
      return state
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

let _id = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation('ui')
  const [toasts, dispatch] = useReducer(reducer, [])
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const isMounted = useRef(false)

  useEffect(() => {
    isMounted.current = true
    const map = timers.current
    return () => {
      isMounted.current = false
      map.forEach(clearTimeout)
      map.clear()
    }
  }, [])

  const dismiss = useCallback((id: string) => {
    const existing = timers.current.get(id)
    if (existing) {
      clearTimeout(existing)
      timers.current.delete(id)
    }
    dispatch({ type: 'START_REMOVE', id })
    // Wait for exit animation (matches animate-toast-out duration)
    setTimeout(() => {
      if (isMounted.current) dispatch({ type: 'REMOVE', id })
    }, 220)
  }, [])

  const dismissAll = useCallback(() => {
    timers.current.forEach((timer, id) => {
      clearTimeout(timer)
      timers.current.delete(id)
    })
    dispatch({ type: 'REMOVE_ALL' })
  }, [])

  const toast = useCallback(
    (options: AddToastOptions) => {
      const id = String(++_id)
      const duration = options.duration ?? DEFAULT_DURATION

      dispatch({ type: 'ADD', payload: { id, duration, ...options } })

      const timer = setTimeout(() => dismiss(id), duration)
      timers.current.set(id, timer)
    },
    [dismiss],
  )

  // Register handler so axios interceptors can surface backend errors via toast
  useEffect(() => {
    setApiErrorHandler((message) => toast({ variant: 'error', title: message }))
    return () => setApiErrorHandler(null)
  }, [toast])

  // Sync locale-aware fallback messages to the pure apiError module
  useEffect(() => {
    setApiErrorFallbacks({
      network: t('ui.error.network'),
      generic: t('ui.error.generic'),
    })
  }, [t])

  return (
    <ToastContext.Provider value={{ toast, dismiss, dismissAll }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Container (portal)
// ---------------------------------------------------------------------------

function ToastContainer({
  toasts,
  onDismiss,
}: {
  toasts: ToastData[]
  onDismiss: (id: string) => void
}) {
  const [mounted, setMounted] = useState(false)
  const { t } = useTranslation('ui')

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted || toasts.length === 0) return null

  return createPortal(
    <section
      aria-label={t('ui.notifications')}
      aria-live="polite"
      aria-relevant="additions removals"
      className={cn(
        'pointer-events-none fixed bottom-4 right-4 z-[9999]',
        'flex flex-col-reverse items-end gap-2',
        'sm:bottom-6 sm:right-6',
      )}
    >
      {toasts.map(t => (
        <Toast key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </section>,
    document.body,
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast must be used inside <ToastProvider>')
  }
  return ctx
}
